// Shareable Authentication Form Component
// Self-contained component that handles both login and registration using AWS Cognito
// Can be used in both main application and Edge extension

import React, { useState, useEffect, useRef } from "react";
import CognitoAuthService, {
  CognitoProfile,
  AuthResult,
  RegistrationResult,
} from "../services/cognitoAuthService";
import { authService } from "../services/authService";
import { apiService } from "../services/apiService";
import { HourglassSpinnerWrapper } from "./HourglassSpinnerWrapper";
import { VerificationForm } from "./VerificationForm";
import { useMessageBox } from "./MessageBox";
import toast from "react-hot-toast";
import "./Auth.css";

/** Drop stale pending-verification entries so tabs and registration stay usable. */
const VERIFICATION_STORAGE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function isVerificationStorageExpired(data: {
  timestamp?: number | string;
}): boolean {
  if (data.timestamp === undefined || data.timestamp === null) {
    return true;
  }
  let ms: number;
  if (typeof data.timestamp === "number") {
    ms = data.timestamp;
  } else {
    ms = Date.parse(String(data.timestamp));
  }
  if (!Number.isFinite(ms)) {
    return true;
  }
  return Date.now() - ms > VERIFICATION_STORAGE_MAX_AGE_MS;
}

/** Read pending verification email; drop corrupt/expired entries immediately (incl. first paint). */
function getPendingVerificationEmailFromStorage(
  storageKey: string,
): string | null {
  try {
    const stored = sessionStorage.getItem(storageKey);
    if (!stored) {
      return null;
    }
    const data = JSON.parse(stored);
    if (!data?.email) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    if (isVerificationStorageExpired(data)) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    return String(data.email);
  } catch {
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // non-critical
    }
    return null;
  }
}

export interface AuthFormProps {
  /** Callback after successful login */
  onLoginSuccess?: (token: string, profile: CognitoProfile) => void;
  /** Callback after successful registration */
  onRegisterSuccess?: (token: string, profile: CognitoProfile) => void;
  /** Custom login function (for extension/main app - uses ExtensionAuthProvider/AuthContext) */
  onLogin?: (email: string, password: string) => Promise<void>;
  /** Custom register function (for extension/main app - uses ExtensionAuthProvider/AuthContext) */
  onRegister?: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
  ) => Promise<{
    needsVerification?: boolean;
    redirectToLogin?: boolean;
    userExists?: boolean;
    isVerified?: boolean;
  } | void>;
  /** Show/hide header with logos */
  showHeader?: boolean;
  /** Compact mode for extension popup */
  compact?: boolean;
  /** Loading state callback */
  onLoadingChange?: (loading: boolean) => void;
  /** Show success message after login (for extension) */
  showSuccessMessage?: boolean;
  /** Initial tab to show: 'login' or 'register' */
  initialTab?: "login" | "register";
}

export const AuthForm: React.FC<AuthFormProps> = ({
  onLoginSuccess,
  onRegisterSuccess,
  onLogin,
  onRegister,
  showHeader = true,
  compact = false,
  onLoadingChange,
  showSuccessMessage = false,
  initialTab = "login",
}) => {
  const { showMessageBox } = useMessageBox();
  const [isLogin, setIsLogin] = useState(initialTab === "login");
  const [isLoading, setIsLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [pendingRegistration, setPendingRegistration] = useState<{
    email: string;
    password?: string;
  } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Removed unused unconfirmedEmail state
  const [pendingUnconfirmedLogin, setPendingUnconfirmedLogin] = useState<{
    email: string;
    password?: string;
  } | null>(null);

  // Ref to track if we're in verification mode (prevents state resets)
  const isVerificationModeRef = useRef(false);

  // Force verification mode - this ensures verification form shows even if other state is reset
  const [forceVerificationMode, setForceVerificationMode] = useState(false);

  // SessionStorage keys for persisting verification state
  const VERIFICATION_STORAGE_KEY = "kairos_pending_verification";

  const persistVerificationState = (email: string) => {
    // Ensure verification mode sticks even if other state resets
    isVerificationModeRef.current = true;
    setForceVerificationMode(true);
    try {
      sessionStorage.setItem(
        VERIFICATION_STORAGE_KEY,
        JSON.stringify({
          email,
          timestamp: Date.now(),
        }),
      );
    } catch (error) {
      // Silently ignore sessionStorage errors (non-critical)
    }
  };

  const abandonPendingVerificationFlow = () => {
    isVerificationModeRef.current = false;
    try {
      sessionStorage.removeItem(VERIFICATION_STORAGE_KEY);
    } catch {
      // non-critical
    }
    setForceVerificationMode(false);
    setNeedsVerification(false);
    setPendingRegistration(null);
    setPendingUnconfirmedLogin(null);
  };

  const switchToLoginTab = () => {
    abandonPendingVerificationFlow();
    setIsLogin(true);
    setSuccessMessage(null);
  };

  const switchToRegisterTab = () => {
    abandonPendingVerificationFlow();
    setIsLogin(false);
    setSuccessMessage(null);
  };

  // Set initial tab based on initialTab prop
  // CRITICAL: Don't reset if we're in verification mode - stay on verification screen
  useEffect(() => {
    // Check if we have verification state in sessionStorage first
    const hasSessionStorage = (() => {
      try {
        return !!sessionStorage.getItem(VERIFICATION_STORAGE_KEY);
      } catch {
        return false;
      }
    })();

    // Only set initial tab if we're NOT in verification mode
    // This ensures verification screen stays visible after sign-up
    if (
      !isVerificationModeRef.current &&
      !forceVerificationMode &&
      !hasSessionStorage
    ) {
      setIsLogin(initialTab === "login");
    }
    // Only re-sync when the URL/modal changes `initialTab`, not when verification mode toggles —
    // otherwise abandoning verification would reset the tab and undo "Create Account".
    // eslint-disable-next-line react-hooks/exhaustive-deps -- forceVerificationMode intentionally excluded; see above
  }, [initialTab]);

  // Load verification state from sessionStorage on mount
  useEffect(() => {
    try {
      const email = getPendingVerificationEmailFromStorage(
        VERIFICATION_STORAGE_KEY,
      );
      if (!email) {
        return;
      }
      setPendingRegistration({ email });
      setNeedsVerification(true);
      setIsLogin(false);
      setForceVerificationMode(true);
      isVerificationModeRef.current = true;
    } catch (error) {
      if (import.meta.env.MODE === "development") {
        console.error(
          "Failed to load verification state from sessionStorage:",
          error,
        );
      }
    }
  }, []); // Only run on mount

  // Login form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Compute form validity - checks both state and refs directly
  // This function is called on every render to catch password manager fills
  const computeFormValidity = () => {
    if (!isLogin) return false;
    const emailValue = email.trim() || emailRef.current?.value?.trim() || "";
    const passwordValue =
      password.trim() || passwordRef.current?.value?.trim() || "";
    return emailValue.length > 0 && passwordValue.length > 0;
  };

  // Update form validity whenever email/password state changes or when checking refs
  // This runs frequently to catch password manager fills
  useEffect(() => {
    if (!isLogin) return; // Only for login form

    const checkValidity = () => {
      // Form validity is computed on render, button checks refs directly
      // No need to update state - component will re-render naturally
      computeFormValidity();
    };

    // Check immediately
    checkValidity();

    // Check multiple times to catch password manager fills (which can happen at various times)
    const timeoutIds = [
      setTimeout(checkValidity, 50),
      setTimeout(checkValidity, 100),
      setTimeout(checkValidity, 200),
      setTimeout(checkValidity, 500),
      setTimeout(checkValidity, 1000),
      setTimeout(checkValidity, 2000),
    ];

    // Also set up an interval to check periodically (for slow password managers)
    // More frequent polling to catch password manager fills
    const intervalId = setInterval(checkValidity, 200);

    // Check immediately when page becomes visible (password managers often fill on focus)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible - check immediately and multiple times
        checkValidity();
        setTimeout(checkValidity, 50);
        setTimeout(checkValidity, 100);
        setTimeout(checkValidity, 200);
      }
    };

    // Check immediately when window gains focus (password managers often fill on focus)
    const handleFocus = () => {
      checkValidity();
      setTimeout(checkValidity, 50);
      setTimeout(checkValidity, 100);
      setTimeout(checkValidity, 200);
    };

    // Listen for visibility changes and focus events
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Use MutationObserver to watch for DOM changes (catches password manager fills)
    // This works even when page is not focused
    let mutationObserver: MutationObserver | null = null;
    if (emailRef.current && passwordRef.current) {
      mutationObserver = new MutationObserver(() => {
        // Check validity when DOM changes (password manager might have filled fields)
        checkValidity();
      });

      // Observe both input fields for attribute and child changes
      mutationObserver.observe(emailRef.current, {
        attributes: true,
        attributeFilter: ["value"],
        childList: false,
        subtree: false,
      });
      mutationObserver.observe(passwordRef.current, {
        attributes: true,
        attributeFilter: ["value"],
        childList: false,
        subtree: false,
      });

      // Also observe the form container for any changes
      const formElement = emailRef.current.closest("form");
      if (formElement) {
        mutationObserver.observe(formElement, {
          attributes: false,
          childList: true,
          subtree: true,
        });
      }
    }

    // Also use a more aggressive approach: check on every animation frame when page is visible
    // This ensures we catch password manager fills even if events don't fire
    let animationFrameId: number | null = null;
    const checkOnAnimationFrame = () => {
      if (!document.hidden) {
        checkValidity();
        animationFrameId = requestAnimationFrame(checkOnAnimationFrame);
      }
    };
    // Start animation frame checking after a short delay to avoid excessive checks
    const startAnimationFrameCheck = setTimeout(() => {
      if (!document.hidden) {
        animationFrameId = requestAnimationFrame(checkOnAnimationFrame);
      }
    }, 1000);

    return () => {
      timeoutIds.forEach((id) => clearTimeout(id));
      clearInterval(intervalId);
      clearTimeout(startAnimationFrameCheck);
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
    };
  }, [email, password, isLogin]);

  // Registration form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const regEmailRef = useRef<HTMLInputElement>(null);
  const regPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isLogin) return;

    const syncAutofilledValues = () => {
      const firstNameValue = firstNameRef.current?.value ?? "";
      const lastNameValue = lastNameRef.current?.value ?? "";
      const emailValue = regEmailRef.current?.value ?? "";
      const passwordValue = regPasswordRef.current?.value ?? "";
      const confirmPasswordValue = confirmPasswordRef.current?.value ?? "";

      if (firstNameValue && firstNameValue !== firstName) {
        setFirstName(firstNameValue);
      }
      if (lastNameValue && lastNameValue !== lastName) {
        setLastName(lastNameValue);
      }
      if (emailValue && emailValue !== regEmail) {
        setRegEmail(emailValue);
      }
      if (passwordValue && passwordValue !== regPassword) {
        setRegPassword(passwordValue);
      }
      if (confirmPasswordValue && confirmPasswordValue !== confirmPassword) {
        setConfirmPassword(confirmPasswordValue);
      }
    };

    syncAutofilledValues();
    const timeoutIds = [
      setTimeout(syncAutofilledValues, 50),
      setTimeout(syncAutofilledValues, 100),
      setTimeout(syncAutofilledValues, 250),
      setTimeout(syncAutofilledValues, 500),
      setTimeout(syncAutofilledValues, 1000),
    ];
    const intervalId = setInterval(syncAutofilledValues, 500);

    const handleInput = () => {
      syncAutofilledValues();
    };

    const fields = [
      firstNameRef.current,
      lastNameRef.current,
      regEmailRef.current,
      regPasswordRef.current,
      confirmPasswordRef.current,
    ];

    fields.forEach((field) => {
      field?.addEventListener("input", handleInput);
      field?.addEventListener("change", handleInput);
    });

    return () => {
      timeoutIds.forEach((id) => clearTimeout(id));
      clearInterval(intervalId);
      fields.forEach((field) => {
        field?.removeEventListener("input", handleInput);
        field?.removeEventListener("change", handleInput);
      });
    };
  }, [confirmPassword, firstName, isLogin, lastName, regEmail, regPassword]);

  // Debug: Log when verification state changes
  useEffect(() => {
    // Check sessionStorage as source of truth (for potential future use)
    // let sessionStorageData = null;
    // try {
    //   const stored = sessionStorage.getItem(VERIFICATION_STORAGE_KEY);
    //   if (stored) {
    //     sessionStorageData = JSON.parse(stored);
    //   }
    // } catch (error) {
    //   // Ignore sessionStorage errors
    // }
    // ONLY check sessionStorage for visibility - ignore React state
    // shouldShow calculated but not used - kept for potential future use
    // const shouldShow = !!sessionStorageData && !!sessionStorageData.email;
  }, [
    needsVerification,
    pendingRegistration,
    isLogin,
    pendingUnconfirmedLogin,
    forceVerificationMode,
  ]);

  // Handle pending unconfirmed login - set verification state after error is caught
  // This runs after React processes the error, ensuring state persists (backup to direct setting)
  useEffect(() => {
    if (pendingUnconfirmedLogin) {
      const email = pendingUnconfirmedLogin.email;

      // CRITICAL: Save to sessionStorage FIRST - this persists across re-renders
      try {
        sessionStorage.setItem(
          VERIFICATION_STORAGE_KEY,
          JSON.stringify({
            email,
            timestamp: Date.now(),
          }),
        );
      } catch (error) {
        // Failed to save verification state - continue anyway
      }

      // Set verification mode ref to prevent state resets
      isVerificationModeRef.current = true;

      // Set force verification mode to ensure form shows
      setForceVerificationMode(true);

      // Set verification state - this is a backup in case direct setting didn't work
      // Only set if not already set correctly
      setPendingRegistration((prev) => {
        if (!prev || prev.email !== email) {
          return { email, password: pendingUnconfirmedLogin.password };
        }
        return prev;
      });

      setNeedsVerification((prev) => {
        if (!prev) {
          return true;
        }
        return prev;
      });

      setIsLogin((prev) => {
        if (prev) {
          return false;
        }
        return prev;
      });

      // Clear pending state after a delay to ensure state is set
      setTimeout(() => {
        setPendingUnconfirmedLogin(null);
      }, 100); // Longer delay to ensure state is persisted
    }
  }, [pendingUnconfirmedLogin]);

  // Detect browser autofill and password manager fills for login form
  useEffect(() => {
    if (!isLogin) return; // Only for login form

    const checkAutofill = () => {
      if (emailRef.current && passwordRef.current) {
        const emailValue = emailRef.current.value;
        const passwordValue = passwordRef.current.value;

        // Update state if values changed
        if (emailValue && emailValue !== email) {
          setEmail(emailValue);
        }
        if (passwordValue && passwordValue !== password) {
          setPassword(passwordValue);
        }

        // Form validity is computed on render, no need to update state
      }
    };

    // Check immediately and multiple times to catch password manager fills
    checkAutofill();
    const timeoutIds = [
      setTimeout(checkAutofill, 50),
      setTimeout(checkAutofill, 100),
      setTimeout(checkAutofill, 200),
      setTimeout(checkAutofill, 300),
      setTimeout(checkAutofill, 500),
      setTimeout(checkAutofill, 1000),
      setTimeout(checkAutofill, 2000),
    ];

    // Event handlers for various autofill triggers
    const handleAnimationStart = (e: AnimationEvent) => {
      if (e.animationName === "onAutoFillStart") {
        checkAutofill();
      }
    };

    const handleInput = () => {
      checkAutofill();
    };

    const handleChange = () => {
      checkAutofill();
    };

    const handleFocus = () => {
      // Check after focus (password managers sometimes fill on focus)
      setTimeout(checkAutofill, 100);
    };

    const handleBlur = () => {
      // Check after blur (password managers sometimes fill after field loses focus)
      setTimeout(checkAutofill, 100);
    };

    // Add event listeners
    document.addEventListener("animationstart", handleAnimationStart);

    // Use a ref callback to ensure we can attach listeners even if refs aren't ready yet
    const attachListeners = () => {
      if (emailRef.current) {
        emailRef.current.addEventListener("input", handleInput);
        emailRef.current.addEventListener("change", handleChange);
        emailRef.current.addEventListener("focus", handleFocus);
        emailRef.current.addEventListener("blur", handleBlur);
      }
      if (passwordRef.current) {
        passwordRef.current.addEventListener("input", handleInput);
        passwordRef.current.addEventListener("change", handleChange);
        passwordRef.current.addEventListener("focus", handleFocus);
        passwordRef.current.addEventListener("blur", handleBlur);
      }
    };

    // Attach listeners immediately and after a delay
    attachListeners();
    setTimeout(attachListeners, 100);

    // Also poll periodically to catch slow password managers
    const pollInterval = setInterval(checkAutofill, 500);

    return () => {
      timeoutIds.forEach((id) => clearTimeout(id));
      clearInterval(pollInterval);
      document.removeEventListener("animationstart", handleAnimationStart);
      if (emailRef.current) {
        emailRef.current.removeEventListener("input", handleInput);
        emailRef.current.removeEventListener("change", handleChange);
        emailRef.current.removeEventListener("focus", handleFocus);
        emailRef.current.removeEventListener("blur", handleBlur);
      }
      if (passwordRef.current) {
        passwordRef.current.removeEventListener("input", handleInput);
        passwordRef.current.removeEventListener("change", handleChange);
        passwordRef.current.removeEventListener("focus", handleFocus);
        passwordRef.current.removeEventListener("blur", handleBlur);
      }
    };
  }, [email, password, isLogin]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    onLoadingChange?.(true);
    setSuccessMessage(null);

    const formData = new FormData(e.currentTarget);
    const emailValue = formData.get("loginEmail") as string;
    const passwordValue = formData.get("loginPassword") as string;

    const startTime = Date.now();
    const minDisplayTime = 2000; // 2 seconds minimum for better UX

    try {
      // Use custom login function if provided (for extension/main app), otherwise call CognitoAuthService directly
      if (onLogin) {
        await onLogin(emailValue, passwordValue);
        // Get token from localStorage after login
        const token = localStorage.getItem("token");

        if (token) {
          // Get profile from API (profile is stored in AuthContext state, not localStorage for security)
          let profile: CognitoProfile | null = null;
          try {
            const apiProfile = await apiService.getProfile();
            // Convert API profile to CognitoProfile format
            profile = {
              id: apiProfile.id,
              email: apiProfile.email,
              firstName: apiProfile.firstName,
              lastName: apiProfile.lastName,
              isActive: apiProfile.isActive ?? true,
              createdAt: apiProfile.createdAt,
              updatedAt: apiProfile.updatedAt,
            };
          } catch (e) {
            // Create minimal profile from token (fallback)
            profile = {
              id: "",
              email: emailValue,
              firstName: "",
              lastName: "",
              isActive: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
          }

          // Call success callback
          if (onLoginSuccess && profile) {
            onLoginSuccess(token, profile);
          }

          // Show success message
          if (showSuccessMessage) {
            setSuccessMessage(
              "Login successful! You can now use the application.",
            );
          } else {
            showMessageBox({
              type: "success",
              title: "Success",
              message: "Login successful!",
              buttons: ["ok"],
              onButtonClick: () => {},
            });
          }
        }
      } else {
        // Call CognitoAuthService directly (for main app)
        const authResult: AuthResult = await CognitoAuthService.login(
          emailValue,
          passwordValue,
        );

        const { idToken, profile } = authResult;
        // API Gateway Cognito User Pool authorizer requires id_token, not access_token
        const token_for_api = idToken || authResult.accessToken;

        // Store token in localStorage (will be synced to extension storage by ExtensionAuthProvider)
        // Profile is stored in AuthContext state, not localStorage (security: prevents XSS exposure)
        localStorage.setItem("token", token_for_api);

        // Call success callback
        if (onLoginSuccess) {
          onLoginSuccess(token_for_api, profile);
        }

        // Show success message
        if (showSuccessMessage) {
          setSuccessMessage(
            "Login successful! You can now use the application.",
          );
        } else {
          // Use MessageBox for extension, toast for main app
          showMessageBox({
            type: "success",
            title: "Success",
            message: "Login successful!",
            buttons: ["ok"],
            onButtonClick: () => {},
          });
        }
      }
    } catch (error) {
      let errorMessage = "Login failed. Please try again.";
      let isUnconfirmed = false;

      if (error instanceof Error) {
        errorMessage = error.message;
        // Check if user is not confirmed - check both error code and message
        const errorCode = (error as any).code || (error as any).name || "";
        const errorMessageLower = error.message.toLowerCase();
        const errorStringLower = String(error).toLowerCase();

        // More comprehensive check for unconfirmed user errors
        const isUnconfirmedError =
          errorCode === "UserNotConfirmedException" ||
          (errorCode === "NotAuthorizedException" &&
            (errorMessageLower.includes("not confirmed") ||
              errorMessageLower.includes("unconfirmed"))) ||
          errorMessageLower.includes("user is not confirmed") ||
          errorMessageLower.includes("usernotconfirmedexception") ||
          errorMessageLower.includes("not confirmed") ||
          errorMessageLower.includes("unconfirmed") ||
          errorStringLower.includes("usernotconfirmedexception") ||
          errorStringLower.includes("not confirmed");

        if (isUnconfirmedError) {
          isUnconfirmed = true;
        }
      }

      if (isUnconfirmed) {
        // Show verification form for unconfirmed user
        // CRITICAL: Save to sessionStorage FIRST - this persists across re-renders
        try {
          sessionStorage.setItem(
            VERIFICATION_STORAGE_KEY,
            JSON.stringify({
              email: emailValue,
              timestamp: Date.now(),
            }),
          );
        } catch (error) {
          // Failed to save verification state - continue anyway
        }

        // Set verification mode ref FIRST to prevent any resets
        isVerificationModeRef.current = true;

        // Set force verification mode - this ensures form shows even if other state is reset
        setForceVerificationMode(true);

        // Set verification state directly - use React's batching to ensure all updates happen together
        // Set pendingUnconfirmedLogin to trigger useEffect as backup
        setPendingUnconfirmedLogin({
          email: emailValue,
          password: passwordValue,
        });

        // Also set state directly to ensure it's set immediately
        setPendingRegistration({ email: emailValue, password: passwordValue });
        setNeedsVerification(true);
        setIsLogin(false);

        showMessageBox({
          type: "error",
          title: "Email Not Verified",
          message:
            "Your email address has not been verified. Please check your email for a verification code or resend it.",
          buttons: ["ok"],
          onButtonClick: () => {},
        });

        // Return early to prevent showing additional error message
        return;
      } else {
        if (compact) {
          showMessageBox({
            type: "error",
            title: "Login Error",
            message: errorMessage,
            buttons: ["ok"],
            onButtonClick: () => {},
          });
        } else {
          toast.error(errorMessage, {
            duration: 5000,
            position: "top-right",
            style: {
              background: "#dc2626",
              color: "#fff",
              fontWeight: "500",
            },
          });
        }
      }
    } finally {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

      setTimeout(() => {
        setIsLoading(false);
        onLoadingChange?.(false);
      }, remainingTime);
    }
  };

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccessMessage(null);

    const formData = new FormData(e.currentTarget);
    const firstNameValue = formData.get("firstName") as string;
    const lastNameValue = formData.get("lastName") as string;
    const emailValue = formData.get("signupEmail") as string;
    const passwordValue = formData.get("signupPassword") as string;
    const confirmPasswordValue = formData.get(
      "signupConfirmPassword",
    ) as string;

    // Validate email format
    if (!authService.isValidEmail(emailValue)) {
      showMessageBox({
        type: "error",
        title: "Validation Error",
        message: "Please enter a valid email address.",
        buttons: ["ok"],
        onButtonClick: () => {},
      });
      setIsLoading(false);
      return;
    }

    // Validate password strength
    const passwordValidation = authService.validatePassword(passwordValue);
    if (!passwordValidation.isValid) {
      showMessageBox({
        type: "error",
        title: "Validation Error",
        message:
          passwordValidation.message || "Password does not meet requirements.",
        buttons: ["ok"],
        onButtonClick: () => {},
      });
      setIsLoading(false);
      return;
    }

    // Validate passwords match
    if (passwordValue !== confirmPasswordValue) {
      showMessageBox({
        type: "error",
        title: "Validation Error",
        message:
          "The passwords you entered do not match. Please make sure both password fields contain the same password.",
        buttons: ["ok"],
        onButtonClick: () => {},
      });
      setIsLoading(false);
      return;
    }

    const startTime = Date.now();
    const minDisplayTime = 5000; // 5 seconds minimum

    try {
      const tokenBefore = localStorage.getItem("token");
      // Use custom register function if provided (for extension/main app), otherwise call CognitoAuthService directly
      if (onRegister) {
        try {
          const result = await onRegister(
            emailValue,
            passwordValue,
            firstNameValue,
            lastNameValue,
          );
          const tokenAfter = localStorage.getItem("token");
          const tokenChanged = !!tokenAfter && tokenAfter !== tokenBefore;

          // Handle result if returned (main app's AuthContext returns result)
          if (result && typeof result === "object") {
            // Handle different registration scenarios
            if (result.redirectToLogin) {
              // User exists and is verified - login successful
              const token = localStorage.getItem("token");
              // Profile should be fetched from API/AuthContext, not localStorage (security)
              // For now, pass null and let AuthContext handle profile loading
              if (token && onRegisterSuccess) {
                onRegisterSuccess(token, null as any); // Profile will be loaded by AuthContext
              }

              if (showSuccessMessage) {
                setSuccessMessage(
                  "Welcome back! You're already registered and verified.",
                );
              } else {
                showMessageBox({
                  type: "success",
                  title: "Success",
                  message:
                    "Welcome back! You're already registered and verified.",
                  buttons: ["ok"],
                  onButtonClick: () => {},
                });
              }
              return;
            }

            // Check if verification is needed
            if (result.needsVerification) {
              // CRITICAL: Set verification state IMMEDIATELY and synchronously before any other operations
              // This ensures the verification form is shown right away
              persistVerificationState(emailValue);
              setPendingRegistration({
                email: emailValue,
                password: passwordValue,
              });
              setNeedsVerification(true);
              setIsLogin(false); // Ensure we're in registration mode, not login mode
              // CRITICAL: Clear loading immediately so full-screen hourglass is hidden and verification form can show
              setIsLoading(false);

              const message = result.userExists
                ? "Account already exists but needs verification. Please check your email for a new verification code."
                : "Registration successful! Please check your email for a verification code.";

              showMessageBox({
                type: "success",
                title: "Verification Required",
                message: message,
                buttons: ["ok"],
                onButtonClick: () => {},
              });
              // CRITICAL: Return early and DO NOT call onRegisterSuccess - modal must stay open
              // The verification form will be shown automatically via the rendering logic
              return; // Don't navigate, show verification form
            }
          }

          // No result returned or registration successful without verification
          // Only treat as success if token changed (prevents stale token from closing modal)
          const token = tokenChanged ? tokenAfter : null;

          if (token) {
            // Profile should be fetched from API/AuthContext, not localStorage (security)
            // AuthContext will load profile automatically after token is set
            let profile: CognitoProfile | null = null;
            try {
              const apiProfile = await apiService.getProfile();
              // Convert API profile to CognitoProfile format
              profile = {
                id: apiProfile.id,
                email: apiProfile.email,
                firstName: apiProfile.firstName,
                lastName: apiProfile.lastName,
                isActive: apiProfile.isActive ?? true,
                createdAt: apiProfile.createdAt,
                updatedAt: apiProfile.updatedAt,
              };
            } catch (e) {
              // Create minimal profile from form data (fallback)
              profile = {
                id: "",
                email: emailValue,
                firstName: firstNameValue,
                lastName: lastNameValue,
                isActive: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              };
            }

            // Call success callback
            if (onRegisterSuccess && profile) {
              onRegisterSuccess(token, profile);
            }

            // Show success message
            if (showSuccessMessage) {
              setSuccessMessage("Registration successful! Welcome to Kairos!");
            } else {
              showMessageBox({
                type: "success",
                title: "Success",
                message: "Registration successful! Welcome to Kairos!",
                buttons: ["ok"],
                onButtonClick: () => {},
              });
            }
            return;
          }

          // Fallback: if no token and no explicit result, assume verification is required
          persistVerificationState(emailValue);
          setPendingRegistration({
            email: emailValue,
            password: passwordValue,
          });
          setNeedsVerification(true);
          setIsLogin(false);
          setIsLoading(false);

          showMessageBox({
            type: "success",
            title: "Verification Required",
            message:
              "Registration successful! Please check your email for a verification code.",
            buttons: ["ok"],
            onButtonClick: () => {},
          });
          return;
        } catch (regError: unknown) {
          // Check if verification is needed (extension throws error with needsVerification flag)
          if (
            regError &&
            typeof regError === "object" &&
            "needsVerification" in regError &&
            (regError as { needsVerification?: boolean }).needsVerification
          ) {
            persistVerificationState(emailValue);
            setPendingRegistration({
              email: emailValue,
              password: passwordValue,
            });
            setNeedsVerification(true);
            setIsLoading(false);

            const message = (regError as { userExists?: boolean }).userExists
              ? "Account already exists but needs verification. Please check your email for a new verification code."
              : "Registration successful! Please check your email for a verification code.";

            showMessageBox({
              type: "success",
              title: "Verification Required",
              message: message,
              buttons: ["ok"],
              onButtonClick: () => {},
            });
            return; // Don't navigate, show verification form
          }
          throw regError; // Re-throw if not a verification error
        }
      } else {
        // Call CognitoAuthService directly (for main app)
        const result: RegistrationResult = await CognitoAuthService.register(
          emailValue,
          passwordValue,
          firstNameValue,
          lastNameValue,
        );

        // Handle different registration scenarios
        if (result.redirectToLogin && result.authResult) {
          // User exists and is verified - login successful
          const { idToken, profile } = result.authResult;
          const token_for_api = idToken || result.authResult.accessToken;

          // Profile is stored in AuthContext state, not localStorage (security: prevents XSS exposure)
          localStorage.setItem("token", token_for_api);

          if (onRegisterSuccess) {
            onRegisterSuccess(token_for_api, profile);
          }

          if (showSuccessMessage) {
            setSuccessMessage(
              "Welcome back! You're already registered and verified.",
            );
          } else {
            showMessageBox({
              type: "success",
              title: "Success",
              message: "Welcome back! You're already registered and verified.",
              buttons: ["ok"],
              onButtonClick: () => {},
            });
          }
          return;
        }

        // Check if verification is needed
        if (result.needsVerification) {
          // CRITICAL: Set verification state IMMEDIATELY and synchronously before any other operations
          // This ensures the verification form is shown right away and stays visible
          persistVerificationState(emailValue);
          setPendingRegistration({
            email: emailValue,
            password: passwordValue,
          });
          setNeedsVerification(true);
          setIsLogin(false); // Ensure we're in registration mode, not login mode
          setIsLoading(false);

          const message = result.userExists
            ? "Account already exists but needs verification. Please check your email for a new verification code."
            : "Registration successful! Please check your email for a verification code.";

          showMessageBox({
            type: "success",
            title: "Verification Required",
            message: message,
            buttons: ["ok"],
            onButtonClick: () => {},
          });
          return; // Don't navigate, show verification form
        }

        // Registration successful without verification (shouldn't happen with Cognito, but handle it)
        if (result.authResult) {
          const { idToken, profile } = result.authResult;
          const token_for_api = idToken || result.authResult.accessToken;

          // Profile is stored in AuthContext state, not localStorage (security: prevents XSS exposure)
          localStorage.setItem("token", token_for_api);

          if (onRegisterSuccess) {
            onRegisterSuccess(token_for_api, profile);
          }

          if (showSuccessMessage) {
            setSuccessMessage(
              "Account created successfully! Welcome to Kairos!",
            );
          } else {
            showMessageBox({
              type: "success",
              title: "Success",
              message: "Account created successfully! Welcome to Kairos!",
              buttons: ["ok"],
              onButtonClick: () => {},
            });
          }
        }
      }
    } catch (error) {
      // Log error in development mode only
      if (import.meta.env.MODE === "development") {
        console.error("❌ AuthForm: Registration failed:", error);
      }

      let errorMessage = "Registration failed. Please try again.";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error && typeof error === "object" && "message" in error) {
        errorMessage = String((error as { message: unknown }).message);
      }

      showMessageBox({
        type: "error",
        title: "Registration Error",
        message: errorMessage,
        buttons: ["ok"],
        onButtonClick: () => {},
      });
    } finally {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

      setTimeout(() => {
        setIsLoading(false);
      }, remainingTime);
    }
  };

  const handleVerificationSuccess = (
    token: string,
    profile: CognitoProfile,
  ) => {
    // CRITICAL: Clear verification state FIRST to prevent any redirects to login
    setNeedsVerification(false);
    setPendingRegistration(null);
    setPendingUnconfirmedLogin(null);

    // Clear verification mode ref and force flag
    isVerificationModeRef.current = false;
    setForceVerificationMode(false);

    // CRITICAL: Clear sessionStorage when verification succeeds
    try {
      sessionStorage.removeItem(VERIFICATION_STORAGE_KEY);
    } catch (error) {
      // Silently ignore sessionStorage errors (non-critical)
      if (import.meta.env.MODE === "development") {
        console.error(
          "Failed to clear verification state from sessionStorage:",
          error,
        );
      }
    }

    // CRITICAL: Call onRegisterSuccess IMMEDIATELY with token and profile
    // This will set auth state and navigate to dashboard, preventing redirect to login
    if (onRegisterSuccess && token && profile) {
      onRegisterSuccess(token, profile);
    }

    if (showSuccessMessage) {
      setSuccessMessage(
        "Email verified and account confirmed! Welcome to Kairos!",
      );
    } else {
      showMessageBox({
        type: "success",
        title: "Success",
        message: "Email verified and account confirmed! Welcome to Kairos!",
        buttons: ["ok"],
        onButtonClick: () => {},
      });
    }
  };

  const handleVerificationSuccessWithoutAutoLogin = (verifiedEmail: string) => {
    setNeedsVerification(false);
    setPendingRegistration(null);
    setPendingUnconfirmedLogin(null);
    isVerificationModeRef.current = false;
    setForceVerificationMode(false);
    setIsLogin(true);
    setEmail(verifiedEmail);
    setPassword("");

    try {
      sessionStorage.removeItem(VERIFICATION_STORAGE_KEY);
    } catch (error) {
      if (import.meta.env.MODE === "development") {
        console.error(
          "Failed to clear verification state from sessionStorage:",
          error,
        );
      }
    }

    showMessageBox({
      type: "success",
      title: "Email Verified",
      message: "Your email has been verified. Please sign in to continue.",
      buttons: ["ok"],
      onButtonClick: () => {},
    });
  };

  return (
    <>
      {isLoading && isLogin && (
        <HourglassSpinnerWrapper
          fullPage={true}
          title={isLogin ? "Signing you in..." : "Creating your account..."}
          subtitle={
            isLogin
              ? "Please wait while we prepare your dashboard"
              : "Please wait while we set up your account"
          }
        />
      )}

      <div
        className={`auth-container ${compact ? "auth-container-compact" : ""} auth-container-no-padding`}
      >
        {showHeader && (
          <div className="auth-header">
            <div className="logo-container cognixis-logo">
              <div className="logo-icon">C</div>
              <span>COGNIXIS</span>
            </div>

            <div className="auth-tabs">
              <div
                className={`auth-tab ${isLogin ? "active" : ""}`}
                onClick={switchToLoginTab}
              >
                Sign In
              </div>
              <div
                className={`auth-tab ${!isLogin ? "active" : ""}`}
                onClick={switchToRegisterTab}
              >
                Create Account
              </div>
            </div>

            <div className="logo-container kairos-logo">
              <div className="logo-icon">K</div>
              <span>KAIROS</span>
            </div>
          </div>
        )}

        {showHeader && <h1 className="sr-only">KAIROS</h1>}

        {successMessage && (
          <div className="success-message">✅ {successMessage}</div>
        )}

        <div
          className={`auth-form-container ${compact ? "auth-form-container-compact" : ""} auth-form-container-no-padding`}
        >
          {/* Show tabs inside form container when header is hidden */}
          {!showHeader && (
            <div className="auth-tabs auth-tabs-inline">
              <div
                className={`auth-tab ${isLogin ? "active" : ""}`}
                onClick={switchToLoginTab}
              >
                Sign In
              </div>
              <div
                className={`auth-tab ${!isLogin ? "active" : ""}`}
                onClick={switchToRegisterTab}
              >
                Create Account
              </div>
            </div>
          )}

          {(() => {
            // SessionStorage is the source of truth when valid; TTL is applied on every read.
            const sessionEmail = getPendingVerificationEmailFromStorage(
              VERIFICATION_STORAGE_KEY,
            );
            const hasSessionVerification = !!sessionEmail;
            const hasStateVerification =
              !!(
                pendingRegistration?.email || pendingUnconfirmedLogin?.email
              ) && needsVerification;
            const hasVerificationData =
              hasSessionVerification || hasStateVerification;
            const verificationEmail =
              sessionEmail ||
              pendingRegistration?.email ||
              pendingUnconfirmedLogin?.email ||
              "";

            // If we have verification data, show the verification form
            if (hasVerificationData) {
              return (
                <VerificationForm
                  key="verification-form"
                  email={verificationEmail}
                  onVerify={async (code: string) => {
                    try {
                      // Get email from sessionStorage first, then fall back to in-memory state.
                      let verifyEmail = "";
                      const verifyPassword =
                        pendingRegistration?.password ||
                        pendingUnconfirmedLogin?.password ||
                        "";

                      try {
                        const stored = sessionStorage.getItem(
                          VERIFICATION_STORAGE_KEY,
                        );
                        if (stored) {
                          const data = JSON.parse(stored);
                          verifyEmail = data.email || "";
                        }
                      } catch (error) {
                        // Fall back to state
                      }

                      if (!verifyEmail) {
                        verifyEmail =
                          pendingRegistration?.email ||
                          pendingUnconfirmedLogin?.email ||
                          "";
                      }

                      if (!verifyEmail) {
                        throw new Error("Email not found for verification");
                      }

                      // Use backend API endpoint to verify email (creates Stripe customer)
                      const verifyResponse = await apiService.verifyEmail(
                        verifyEmail,
                        code,
                      );

                      if (verifyPassword) {
                        // After verification, login the user when the password is still available in memory.
                        const authResult = await CognitoAuthService.login(
                          verifyEmail,
                          verifyPassword,
                        );

                        const { idToken, profile } = authResult;
                        const token_for_api = idToken || authResult.accessToken;

                        // CRITICAL: Update tokens in all services BEFORE calling handleVerificationSuccess
                        // This ensures API calls work immediately after verification
                        localStorage.setItem("token", token_for_api);
                        localStorage.setItem(
                          "profile",
                          JSON.stringify(profile),
                        );
                        apiService.setToken(token_for_api);

                        // Update secureOpenAIService token if available
                        try {
                          const { secureOpenAIService } = await import(
                            "../services/secureOpenaiService"
                          );
                          secureOpenAIService.setToken(token_for_api);
                        } catch (error) {
                          // Silently ignore if secureOpenAIService is not available
                          if (import.meta.env.MODE === "development") {
                            console.warn(
                              "secureOpenAIService not available:",
                              error,
                            );
                          }
                        }

                        // Store Stripe customer ID if returned
                        if (verifyResponse.stripeCustomerId) {
                          // Store in profile or separate storage if needed
                        }

                        // CRITICAL: Call handleVerificationSuccess which will trigger onRegisterSuccess
                        // This ensures the user is logged in and redirected to dashboard
                        handleVerificationSuccess(token_for_api, profile);
                      } else {
                        handleVerificationSuccessWithoutAutoLogin(verifyEmail);
                      }
                    } catch (error: unknown) {
                      // Log error in development mode only
                      if (import.meta.env.MODE === "development") {
                        console.error(
                          "❌ AuthForm: Verification failed:",
                          error,
                        );
                      }
                      let errorMessage =
                        "Verification failed. Please check your code and try again.";
                      if (error instanceof Error) {
                        errorMessage = error.message;
                      }

                      // Handle Stripe-related errors
                      if (
                        errorMessage.includes("Stripe") ||
                        errorMessage.includes("STRIPE")
                      ) {
                        errorMessage =
                          "Account verification failed due to a billing system error. Please contact support if this issue persists.";
                      }

                      showMessageBox({
                        type: "error",
                        title: "Verification Error",
                        message: errorMessage,
                        buttons: ["ok"],
                        onButtonClick: () => {},
                      });
                      throw error;
                    }
                  }}
                  onResendCode={async () => {
                    try {
                      // Get email from sessionStorage first (most reliable), then state
                      let resendEmail = "";

                      try {
                        const stored = sessionStorage.getItem(
                          VERIFICATION_STORAGE_KEY,
                        );
                        if (stored) {
                          const data = JSON.parse(stored);
                          resendEmail = data.email || "";
                        }
                      } catch (error) {
                        // Fall back to state
                      }

                      if (!resendEmail) {
                        resendEmail =
                          pendingRegistration?.email ||
                          pendingUnconfirmedLogin?.email ||
                          "";
                      }

                      if (!resendEmail) {
                        throw new Error(
                          "Email not found for resending verification code",
                        );
                      }

                      // Try using authService first (backend API), fallback to CognitoAuthService
                      try {
                        await authService.resendVerification(resendEmail);
                        showMessageBox({
                          type: "success",
                          title: "Code Resent",
                          message:
                            "A new verification code has been sent to your email.",
                          buttons: ["ok"],
                          onButtonClick: () => {},
                        });
                      } catch (backendError) {
                        // Fallback to CognitoAuthService if backend fails
                        await CognitoAuthService.resendVerificationCode(
                          resendEmail,
                        );
                        showMessageBox({
                          type: "success",
                          title: "Code Resent",
                          message:
                            "A new verification code has been sent to your email.",
                          buttons: ["ok"],
                          onButtonClick: () => {},
                        });
                      }
                    } catch (error: unknown) {
                      const errorMessage =
                        error instanceof Error
                          ? error.message
                          : "Unknown error";
                      showMessageBox({
                        type: "error",
                        title: "Error",
                        message: `Failed to resend code: ${errorMessage}`,
                        buttons: ["ok"],
                        onButtonClick: () => {},
                      });
                      throw error;
                    }
                  }}
                  isLoading={isLoading}
                />
              );
            }

            // Otherwise, show login or register form
            return isLogin ? (
              <form
                id="auth-login-form"
                className="auth-form auth-form-no-margin"
                onSubmit={handleLogin}
              >
                <div className="form-group">
                  <label className="sr-only" htmlFor="login-email">
                    Email
                  </label>
                  <input
                    ref={emailRef}
                    type="email"
                    id="login-email"
                    name="loginEmail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    autoComplete="section-login username"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label className="sr-only" htmlFor="login-password">
                    Password
                  </label>
                  <input
                    ref={passwordRef}
                    type="password"
                    id="login-password"
                    name="loginPassword"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="section-login current-password"
                    required
                    disabled={isLoading}
                  />
                </div>

                <button
                  type="submit"
                  className="auth-button auth-button-no-margin"
                  disabled={isLoading || !computeFormValidity()}
                >
                  {isLoading ? "Signing In..." : "Sign In"}
                </button>

                <a
                  href="#"
                  className="forgot-password-link"
                  onClick={(e) => e.preventDefault()}
                >
                  Forgot your password?
                </a>
              </form>
            ) : (
              <form
                id="auth-register-form"
                className="auth-form auth-form-no-margin"
                onSubmit={handleRegister}
              >
                <div className="form-group">
                  <label className="sr-only" htmlFor="signup-email">
                    Email
                  </label>
                  <input
                    ref={regEmailRef}
                    type="email"
                    id="signup-email"
                    name="signupEmail"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="Enter your email"
                    autoComplete="section-credentials username"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label className="sr-only" htmlFor="signup-password">
                    Password
                  </label>
                  <input
                    ref={regPasswordRef}
                    type="password"
                    id="signup-password"
                    name="signupPassword"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    placeholder="Create a password"
                    autoComplete="section-credentials new-password"
                    minLength={8}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label className="sr-only" htmlFor="signup-confirm-password">
                    Confirm Password
                  </label>
                  <input
                    ref={confirmPasswordRef}
                    type="password"
                    id="signup-confirm-password"
                    name="signupConfirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    autoComplete="section-credentials new-password"
                    minLength={8}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label className="sr-only" htmlFor="signup-first-name">
                    First Name
                  </label>
                  <input
                    ref={firstNameRef}
                    type="text"
                    id="signup-first-name"
                    name="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter your first name"
                    autoComplete="section-profile given-name"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label className="sr-only" htmlFor="signup-last-name">
                    Last Name
                  </label>
                  <input
                    ref={lastNameRef}
                    type="text"
                    id="signup-last-name"
                    name="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter your last name"
                    autoComplete="section-profile family-name"
                    required
                    disabled={isLoading}
                  />
                </div>

                <button
                  type="submit"
                  className="auth-button auth-button-no-margin"
                  disabled={
                    isLoading ||
                    !firstName ||
                    !lastName ||
                    !regEmail ||
                    !regPassword ||
                    !confirmPassword ||
                    regPassword !== confirmPassword
                  }
                >
                  {isLoading ? "Creating Account..." : "Create Account"}
                </button>
              </form>
            );
          })()}
        </div>
      </div>
    </>
  );
};
